import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cpu, MapPin, MessageCircle, PhoneCall, Settings, TimerReset, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditableText } from "./TextEditor";
import { CONTACT, DEFAULT_WHATSAPP_MESSAGE, DIVISION_ROTATION_SLOT_MAP, getWhatsAppUrl, SOCIAL_LINKS } from "./config";
import { CoverageSection, OperationalAssetsSection } from "./FleetCoverage";
import { ComplexRecoverySection, HeavyRecoverySection, LightDutySection, MobileWorkshopSection, RotationDots, buildSectionImages, useRotatingImages } from "./OperationsSections";
import { Footer, MobileEmergencyBar } from "./RequestFooter";
import { Navbar } from "./Navbar";
import { HeroSection, ServicesSection } from "./HeroSections";

const meccatronicaHeroImage = "https://static.prod-images.emergentagent.com/jobs/8ad3288b-acff-428d-904d-27a1bd011141/images/353e392f0090fd31e5996ea06d378969b3a072c4a0ff3832ef03d705aba81b95.png";
const gommistaHeroImage = "https://static.prod-images.emergentagent.com/jobs/8ad3288b-acff-428d-904d-27a1bd011141/images/d10fa8d811ad8c1bab486e2f1fc361e6e80fee6535408fb3e96813bdfd3209d6.png";
const soccorsoHeroImage = "https://customer-assets.emergentagent.com/job_soccorso-messina/artifacts/tbry4sru_1%20%2818%29.jpeg";
const meccatronicaCardImage = "/api/viscar/asset-proxy?url=https%3A%2F%2Fcustomer-assets.emergentagent.com%2Fjob_soccorso-messina%2Fartifacts%2Fnwlozhq3_5%2520%252811%2529.jpeg";
const gommistaCardImage = "/api/viscar/asset-proxy?url=https%3A%2F%2Fcustomer-assets.emergentagent.com%2Fjob_soccorso-messina%2Fartifacts%2F00llatc7_5%2520%25282%2529.jpeg";
const contattiHeroImage = "https://images.pexels.com/photos/13062478/pexels-photo-13062478.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const meccatronicaDetailImage = "https://images.pexels.com/photos/4116193/pexels-photo-4116193.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const meccatronicaSupportImage = "https://images.pexels.com/photos/6870326/pexels-photo-6870326.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const gommistaDetailImage = "https://images.pexels.com/photos/6720515/pexels-photo-6720515.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const gommistaSupportImage = "https://images.pexels.com/photos/6720507/pexels-photo-6720507.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const socialIconMap = {
  facebook: (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M13.5 21v-7h2.4l.4-2.8h-2.8V9.4c0-.8.2-1.4 1.4-1.4H16V5.5c-.2 0-.9-.1-1.8-.1-1.8 0-3.1 1.1-3.1 3.3v1.9H9v2.8h2.3v7h2.2Z" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M7.5 3h9A4.5 4.5 0 0 1 21 7.5v9a4.5 4.5 0 0 1-4.5 4.5h-9A4.5 4.5 0 0 1 3 16.5v-9A4.5 4.5 0 0 1 7.5 3Zm0 1.8A2.7 2.7 0 0 0 4.8 7.5v9a2.7 2.7 0 0 0 2.7 2.7h9a2.7 2.7 0 0 0 2.7-2.7v-9a2.7 2.7 0 0 0-2.7-2.7h-9Zm9.45 1.35a1.05 1.05 0 1 1 0 2.1 1.05 1.05 0 0 1 0-2.1ZM12 7.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 7.5Zm0 1.8A2.7 2.7 0 1 0 14.7 12 2.7 2.7 0 0 0 12 9.3Z" />
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M14.9 3c.3 2 1.5 3.6 3.4 4.5.8.4 1.7.6 2.7.6v2.8c-1.4 0-2.8-.3-4-.9v5.5a5.5 5.5 0 1 1-5.5-5.5c.4 0 .8 0 1.2.1v2.9a2.7 2.7 0 1 0 1.9 2.5V3h2.3Z" />
    </svg>
  ),
};

const setPageMeta = (title, description) => {
  document.title = title;
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", description);
  } else {
    const meta = document.createElement("meta");
    meta.name = "description";
    meta.content = description;
    document.head.appendChild(meta);
  }
};

const PageShell = ({ children, testId, className = "" }) => (
  <main data-testid={testId} className={`viscar-site viscar-site--inner ${className}`.trim()}>
    <Navbar />
    {children}
    <Footer />
    <MobileEmergencyBar />
  </main>
);

const OperationalPageHero = ({ image, eyebrow, title, description, badges, textPrefix, phoneHref = CONTACT.roadsideHref, whatsappNumber = CONTACT.roadsideWhatsAppNumber }) => (
  <section data-testid={`${textPrefix}-hero`} className="section division-hero" style={{ "--division-hero-image": `url(${image})` }}>
    <div className="division-hero__overlay" />
    <div className="container division-hero__content">
      <div className="division-hero__copy">
        <EditableText textId={`${textPrefix}.hero.eyebrow`} defaultText={eyebrow} as="span" className="section-eyebrow" testId={`${textPrefix}-hero-eyebrow`} />
        <EditableText textId={`${textPrefix}.hero.title`} defaultText={title} as="h1" testId={`${textPrefix}-hero-title`} />
        <EditableText textId={`${textPrefix}.hero.description`} defaultText={description} as="p" testId={`${textPrefix}-hero-description`} multiline />
        <div data-testid={`${textPrefix}-hero-badges`} className="badge-row badge-row--large">
          {badges.map((badge, index) => (
            <span data-testid={`${textPrefix}-hero-badge-${index}`} key={badge}>{badge}</span>
          ))}
        </div>
        <div data-testid={`${textPrefix}-hero-actions`} className="section-actions">
          <Button data-testid={`${textPrefix}-hero-call`} asChild className="btn-emergency"><a href={phoneHref}><PhoneCall size={18} /> Chiama ora</a></Button>
          <Button data-testid={`${textPrefix}-hero-whatsapp`} asChild className="btn-whatsapp"><a href={getWhatsAppUrl(DEFAULT_WHATSAPP_MESSAGE, whatsappNumber)} target="_blank" rel="noreferrer"><MessageCircle size={18} /> WhatsApp H24</a></Button>
        </div>
      </div>
    </div>
  </section>
);

const OverviewCard = ({ item, index, prefix, className = "" }) => {
  const Icon = item.icon;
  return (
    <article data-testid={`${prefix}-card-${index}`} className={`division-overview-card tech-card ${className}`.trim()}>
      <div className="service-meta"><span>{item.badge}</span></div>
      <Icon data-testid={`${prefix}-card-icon-${index}`} className="service-icon" size={28} />
      <EditableText textId={`${prefix}.card.${index}.title`} defaultText={item.title} as="h3" testId={`${prefix}-card-title-${index}`} />
      <EditableText textId={`${prefix}.card.${index}.description`} defaultText={item.description} as="p" testId={`${prefix}-card-description-${index}`} multiline />
    </article>
  );
};

const RotatingDivisionMedia = ({ imageSlots, slotKeys, fallbackImage, alt, testId, imageTestId, dotsPrefix, className }) => {
  const images = useMemo(() => {
    const rotated = buildSectionImages(imageSlots, slotKeys);
    if (rotated.length) return rotated;
    return fallbackImage ? [fallbackImage] : [];
  }, [fallbackImage, imageSlots, slotKeys]);

  const { activeImage, activeIndex } = useRotatingImages(images);

  return (
    <div data-testid={testId} className={`${className} rotation-media-frame`}>
      {activeImage ? (
        <img data-testid={imageTestId} src={activeImage} alt={alt} />
      ) : (
        <div data-testid={`${imageTestId}-placeholder`} className="empty-photo-placeholder empty-photo-placeholder--wide">&nbsp;</div>
      )}
      <RotationDots testIdPrefix={dotsPrefix} images={images} activeIndex={activeIndex} />
    </div>
  );
};

export const BusinessAreasSection = () => {
  const areas = [
    {
      title: "Soccorso H24",
      badge: "H24 OPERATION",
      description: "Macro-area operativa dedicata a soccorso stradale, heavy recovery, officina mobile e flotta H24.",
      href: "/soccorso-h24",
      image: soccorsoHeroImage,
    },
    {
      title: "Meccatronica",
      badge: "TECH WORKSHOP",
      description: "Diagnosi elettronica, centraline, manutenzione e supporto tecnico con struttura officina dedicata.",
      href: "/meccatronica",
      image: meccatronicaCardImage,
    },
    {
      title: "Gommista",
      badge: "TRUCK TIRE SERVICE",
      description: "Servizio pneumatici, cambio gomme, equilibratura e assistenza gomme per leggero, commerciale e truck.",
      href: "/gommista",
      image: gommistaCardImage,
    },
  ];

  return (
    <section data-testid="business-areas-section" className="section business-areas-section">
      <div className="container">
        <div data-testid="business-areas-header" className="section-header section-header--wide">
          <EditableText textId="business.areas.eyebrow" defaultText="Struttura Viscar" as="span" className="section-eyebrow" testId="business-areas-eyebrow" />
          <EditableText textId="business.areas.title" defaultText="Tre aree chiare: soccorso operativo, meccatronica e gommista" as="h2" testId="business-areas-title" />
          <EditableText textId="business.areas.description" defaultText="Viscar distingue l’operatività H24 su strada dalla struttura tecnica aziendale e dai servizi specialistici pneumatici, con una navigazione più semplice e professionale." as="p" testId="business-areas-description" multiline />
        </div>

        <div data-testid="business-areas-grid" className="business-areas-grid">
          {areas.map((area, index) => (
            <article data-testid={`business-area-card-${index}`} key={area.title} className="business-area-card">
              <div className="business-area-card__media">
                <img data-testid={`business-area-card-image-${index}`} src={area.image} alt={area.title} />
              </div>
              <div className="business-area-card__content">
                <span data-testid={`business-area-card-badge-${index}`} className="card-tag">{area.badge}</span>
                <EditableText textId={`business.area.${index}.title`} defaultText={area.title} as="h3" testId={`business-area-card-title-${index}`} />
                <EditableText textId={`business.area.${index}.description`} defaultText={area.description} as="p" testId={`business-area-card-description-${index}`} multiline />
                <Button data-testid={`business-area-card-link-${index}`} asChild className="btn-ghost-tech">
                  <Link to={area.href}>Apri area <ArrowRight size={16} /></Link>
                </Button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export const SoccorsoH24Page = ({ imageSlots, fleet, galleryPhotos }) => {
  useEffect(() => {
    setPageMeta(
      "Soccorso H24, recovery e officina mobile | Viscar",
      "Area operativa Viscar dedicata a soccorso pesante, officina mobile, recuperi complessi e flotta H24 su Messina, A18 e A20.",
    );
  }, []);

  return (
    <PageShell testId="soccorso-h24-page">
      <HeroSection imageSlots={imageSlots} fallbackHeroImage={soccorsoHeroImage} />
      <ServicesSection />
      <LightDutySection imageSlots={imageSlots} />
      <HeavyRecoverySection imageSlots={imageSlots} />
      <MobileWorkshopSection imageSlots={imageSlots} />
      <ComplexRecoverySection imageSlots={imageSlots} />
      <CoverageSection />
      <OperationalAssetsSection interventionPhotos={galleryPhotos} fleet={fleet} />
    </PageShell>
  );
};

export const AdvertisingHomePage = ({ imageSlots }) => {
  useEffect(() => {
    setPageMeta(
      "Viscar soccorso stradale H24 | Carroattrezzi, recovery e officina a Messina",
      "Pagina introduttiva Viscar dedicata a carroattrezzi, soccorso stradale H24, recovery, officina mobile, A18, A20, Messina e provincia.",
    );
  }, []);

  return (
    <PageShell testId="advertising-home-page">
      <HeroSection imageSlots={imageSlots} fallbackHeroImage={soccorsoHeroImage} />
      <BusinessAreasSection />
    </PageShell>
  );
};

export const MeccatronicaPage = ({ imageSlots = {} }) => {
  useEffect(() => {
    setPageMeta(
      "Meccatronica e diagnosi elettronica | Viscar",
      "Officina tecnica Viscar dedicata a diagnosi elettronica, centraline, manutenzione, riparazioni e supporto tecnico professionale.",
    );
  }, []);

  const features = [
    { icon: Cpu, badge: "DIAGNOSTICS", title: "Diagnosi elettronica", description: "Analisi parametri, ricerca guasti e lettura anomalie con approccio ordinato e professionale." },
    { icon: Settings, badge: "ECU SUPPORT", title: "Centraline e sistemi", description: "Verifica centraline, sensori e gestione delle principali criticità elettroniche del veicolo." },
    { icon: Wrench, badge: "WORKSHOP", title: "Meccanica e manutenzione", description: "Riparazioni mirate, manutenzione tecnica e lavorazioni coerenti con una struttura officina evoluta." },
    { icon: TimerReset, badge: "UPTIME", title: "Continuità operativa", description: "Obiettivo chiaro: ridurre il fermo mezzo e riportare il veicolo in operatività con tempi credibili." },
  ];

  return (
    <PageShell testId="meccatronica-page" className="viscar-site--workshop">
      <OperationalPageHero
        image={imageSlots.meccatronicaHero || meccatronicaHeroImage}
        eyebrow="Meccatronica"
        title="Officina tecnica, diagnosi elettronica e meccanica professionale"
        description="Reparto tecnico Viscar dedicato a diagnosi, centraline, manutenzione e lavorazioni di officina in un ambiente ordinato, credibile e professionale."
        badges={["Diagnostics", "ECU Support", "Workshop", "Uptime"]}
        textPrefix="meccatronica"
        phoneHref={CONTACT.workshopHref}
        whatsappNumber={CONTACT.workshopWhatsAppNumber}
      />

      <section data-testid="meccatronica-overview-section" className="section division-section division-section--technical">
        <div className="container">
          <div data-testid="meccatronica-overview-header" className="section-header section-header--wide">
            <EditableText textId="meccatronica.overview.eyebrow" defaultText="Officina Viscar" as="span" className="section-eyebrow" />
            <EditableText textId="meccatronica.overview.title" defaultText="Una struttura tecnica pulita, leggibile e orientata al lavoro reale" as="h2" />
            <EditableText textId="meccatronica.overview.description" defaultText="Qui Viscar esce dal solo perimetro dell’emergenza e si presenta come officina tecnica: diagnosi, elettronica, meccanica e metodo operativo." as="p" multiline />
          </div>
          <div data-testid="meccatronica-overview-grid" className="division-overview-grid division-overview-grid--editorial">
            {features.map((item, index) => <OverviewCard key={item.title} item={item} index={index} prefix="meccatronica" className="division-overview-card--editorial" />)}
          </div>
        </div>
      </section>

      <section data-testid="meccatronica-structure-section" className="section division-split-section">
        <div className="container division-split-grid">
          <RotatingDivisionMedia
            imageSlots={imageSlots}
            slotKeys={DIVISION_ROTATION_SLOT_MAP.meccatronicaDetail}
            fallbackImage={meccatronicaDetailImage}
            alt="Diagnosi elettronica e supporto tecnico Viscar"
            testId="meccatronica-structure-media"
            imageTestId="meccatronica-structure-active-image"
            dotsPrefix="meccatronica-structure"
            className="division-split-media"
          />
          <div data-testid="meccatronica-structure-copy" className="split-content">
            <EditableText textId="meccatronica.structure.eyebrow" defaultText="Diagnosi e meccanica" as="span" className="section-eyebrow" />
            <EditableText textId="meccatronica.structure.title" defaultText="Centraline, diagnosi e lavorazioni tecniche con linguaggio da officina" as="h2" />
            <EditableText textId="meccatronica.structure.description" defaultText="Il reparto meccatronica Viscar si occupa di diagnosi elettronica, controllo centraline, verifiche tecniche e lavorazioni meccaniche svolte con metodo, ordine e competenza di officina." as="p" multiline />
            <div data-testid="meccatronica-structure-badges" className="badge-row badge-row--large">
              {["Diagnostics", "Centraline", "Workshop", "Mechanical Service"].map((badge, index) => <span data-testid={`meccatronica-structure-badge-${index}`} key={badge}>{badge}</span>)}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="meccatronica-support-section" className="section division-cta-section">
        <div className="container division-cta-panel">
          <div data-testid="meccatronica-support-copy" className="split-content">
            <EditableText textId="meccatronica.support.eyebrow" defaultText="Assistenza officina" as="span" className="section-eyebrow" />
            <EditableText textId="meccatronica.support.title" defaultText="Supporto tecnico per privati, aziende e mezzi da lavoro" as="h2" />
            <EditableText textId="meccatronica.support.description" defaultText="Viscar affianca all’attività di soccorso H24 una struttura tecnica in grado di seguire diagnosi, manutenzione e interventi di officina per privati, aziende e mezzi da lavoro." as="p" multiline />
            <div data-testid="meccatronica-support-actions" className="section-actions">
              <Button data-testid="meccatronica-support-whatsapp" asChild className="btn-ghost-tech"><a href={getWhatsAppUrl(DEFAULT_WHATSAPP_MESSAGE, CONTACT.workshopWhatsAppNumber)} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Apri WhatsApp</a></Button>
            </div>
          </div>
          <RotatingDivisionMedia
            imageSlots={imageSlots}
            slotKeys={DIVISION_ROTATION_SLOT_MAP.meccatronicaSupport}
            fallbackImage={meccatronicaSupportImage}
            alt="Tecnico officina Viscar con tablet diagnostico"
            testId="meccatronica-support-media"
            imageTestId="meccatronica-support-active-image"
            dotsPrefix="meccatronica-support"
            className="division-cta-media"
          />
        </div>
      </section>

    </PageShell>
  );
};

export const GommistaPage = ({ imageSlots = {} }) => {
  useEffect(() => {
    setPageMeta(
      "Servizio gomme e assistenza pneumatici | Viscar",
      "Pneumatici, sostituzione gomme e supporto tecnico per auto, furgoni, autocarri e mezzi da lavoro.",
    );
  }, []);

  return (
    <PageShell testId="gommista-page" className="viscar-site--workshop">
      <OperationalPageHero
        image={imageSlots.gommistaHero || gommistaHeroImage}
        eyebrow="Gommista"
        title="Servizio gomme e assistenza pneumatici"
        description="Pneumatici, sostituzione gomme e supporto tecnico per auto, furgoni, autocarri e mezzi da lavoro."
        badges={["Pneumatici", "Truck Service", "Supporto Tecnico"]}
        textPrefix="gommista"
        phoneHref={CONTACT.workshopHref}
        whatsappNumber={CONTACT.workshopWhatsAppNumber}
      />

      <section data-testid="gommista-structure-section" className="section division-split-section">
        <div className="container division-split-grid">
          <RotatingDivisionMedia
            imageSlots={imageSlots}
            slotKeys={DIVISION_ROTATION_SLOT_MAP.gommistaDetail}
            fallbackImage={gommistaDetailImage}
            alt="Tecnico gommista Viscar con pneumatico truck"
            testId="gommista-structure-media"
            imageTestId="gommista-structure-active-image"
            dotsPrefix="gommista-structure"
            className="division-split-media"
          />
          <div data-testid="gommista-structure-copy" className="split-content">
            <EditableText textId="gommista.structure.eyebrow" defaultText="Servizio operativo" as="span" className="section-eyebrow" />
            <EditableText textId="gommista.structure.title" defaultText="Pneumatici truck, cambio gomme autocarro e assistenza operativa" as="h2" />
            <EditableText textId="gommista.structure.description" defaultText="Il reparto gommista Viscar supporta auto, veicoli commerciali e autocarri con attrezzatura dedicata, assistenza pneumatici e cambio gomme autocarro con approccio operativo e professionale." as="p" multiline />
            <div data-testid="gommista-structure-badges" className="badge-row badge-row--large">
              {["Truck Service", "Cambio gomme autocarro", "Pneumatici"].map((badge, index) => <span data-testid={`gommista-structure-badge-${index}`} key={badge}>{badge}</span>)}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="gommista-support-section" className="section division-cta-section">
        <div className="container division-cta-panel">
          <div data-testid="gommista-support-copy" className="split-content">
            <EditableText textId="gommista.support.eyebrow" defaultText="Clienti e mezzi" as="span" className="section-eyebrow" />
            <EditableText textId="gommista.support.title" defaultText="Supporto pneumatici per privati, aziende e autotrasportatori" as="h2" />
            <EditableText textId="gommista.support.description" defaultText="Servizi dedicati ad auto, veicoli commerciali, autocarri e mezzi industriali, con maggiore attenzione alle esigenze truck e al cambio gomme autocarro." as="p" multiline />
            <div data-testid="gommista-support-actions" className="section-actions">
              <Button data-testid="gommista-support-whatsapp" asChild className="btn-ghost-tech"><a href={getWhatsAppUrl(DEFAULT_WHATSAPP_MESSAGE, CONTACT.workshopWhatsAppNumber)} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Apri WhatsApp</a></Button>
            </div>
          </div>
          <RotatingDivisionMedia
            imageSlots={imageSlots}
            slotKeys={DIVISION_ROTATION_SLOT_MAP.gommistaSupport}
            fallbackImage={gommistaSupportImage}
            alt="Assistenza pneumatici truck Viscar in officina"
            testId="gommista-support-media"
            imageTestId="gommista-support-active-image"
            dotsPrefix="gommista-support"
            className="division-cta-media"
          />
        </div>
      </section>

    </PageShell>
  );
};

export const ContattiPage = () => {
  useEffect(() => {
    setPageMeta(
      "Contatti Viscar | Officina e soccorso stradale H24",
      "Contatti Viscar a Messina per officina, meccatronica, assistenza tecnica e soccorso stradale H24 su Messina, A18 e A20.",
    );
  }, []);

  const contactCards = [
    {
      key: "where",
      eyebrow: "Dove ci troviamo",
      title: "DOVE CI TROVIAMO",
      description: "Siamo a Messina, nella zona industriale di Larderia – Polo ASI, Capannone 18, a pochi minuti dallo svincolo autostradale di Tremestieri. Una posizione strategica che ci permette di offrire soccorso stradale H24, servizi di meccatronica, assistenza tecnica e supporto specializzato per auto, furgoni, autocarri e mezzi da lavoro.",
      icon: MapPin,
      actionLabel: "Apri Google Maps",
      actionHref: "https://maps.app.goo.gl/6GmiBXesGfY9CEfcA",
      actionType: "external",
    },
    {
      key: "officina",
      eyebrow: "Contatto officina",
      title: "CONTATTO OFFICINA",
      description: "Per informazioni, lavorazioni di officina, diagnosi elettronica e assistenza tecnica è possibile contattare direttamente la struttura Viscar.",
      icon: Wrench,
      actionLabel: "Chiama officina",
      actionHref: CONTACT.workshopHref,
      actionType: "link",
    },
    {
      key: "soccorso",
      eyebrow: "Contatto soccorso stradale",
      title: "CONTATTO SOCCORSO STRADALE",
      description: "Servizio di soccorso stradale H24 operativo su Messina, A18 e A20 per interventi su strada, autostrada e assistenza mezzi leggeri e pesanti.",
      icon: PhoneCall,
      actionLabel: "Chiama soccorso",
      actionHref: CONTACT.roadsideHref,
      actionType: "link",
    },
  ];

  const socialCards = SOCIAL_LINKS;

  return (
    <PageShell testId="contatti-page">
      <section data-testid="contatti-hero" className="section division-hero contatti-hero" style={{ "--contatti-hero-image": `url(${contattiHeroImage})` }}>
        <div className="division-hero__overlay" />
        <div className="container division-hero__content">
          <div className="division-hero__copy" />
        </div>
      </section>

      <section data-testid="contatti-cards-section" className="section division-section division-section--technical contatti-cards-section">
        <div className="container">
          <div data-testid="contatti-cards-grid" className="contatti-cards-grid">
            {contactCards.map((card, index) => {
              const Icon = card.icon;

              return (
                <article data-testid={`contatti-card-${card.key}`} key={card.key} className="contatti-card tech-card">
                  <div data-testid={`contatti-card-header-${card.key}`} className="contatti-card__header">
                    <div className="service-meta"><span>{card.eyebrow}</span></div>
                    <Icon data-testid={`contatti-card-icon-${index}`} className="service-icon contatti-card__icon" size={20} />
                  </div>
                  <EditableText textId={`contatti.card.${card.key}.description`} defaultText={card.description} as="p" testId={`contatti-card-description-${card.key}`} multiline />
                  <div className="section-actions">
                    {card.actionType === "route" ? (
                      <Button data-testid={`contatti-card-action-${card.key}`} asChild className="btn-ghost-tech"><Link to={card.actionHref}>{card.actionLabel}</Link></Button>
                    ) : card.actionType === "external" ? (
                      <Button data-testid={`contatti-card-action-${card.key}`} asChild className="btn-ghost-tech"><a href={card.actionHref} target="_blank" rel="noreferrer">{card.actionLabel}</a></Button>
                    ) : (
                      <Button data-testid={`contatti-card-action-${card.key}`} asChild className="btn-emergency"><a href={card.actionHref}>{card.actionLabel}</a></Button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="contatti-social-section" className="section contatti-social-section">
        <div className="container">
          <div data-testid="contatti-social-header" className="section-header section-header--wide">
            <EditableText textId="contatti.social.eyebrow" defaultText="Canali social" as="span" className="section-eyebrow" testId="contatti-social-eyebrow" />
            <EditableText textId="contatti.social.title" defaultText="Seguici anche sui social" as="h2" testId="contatti-social-title" />
            <EditableText textId="contatti.social.description" defaultText="Seguici per restare aggiornati sulle attività Viscar e sui contenuti pubblicati sui canali social ufficiali" as="p" testId="contatti-social-description" multiline />
          </div>
          <div data-testid="contatti-social-grid" className="contatti-social-grid">
            {socialCards.map((item) => (
              <article data-testid={`contatti-social-card-${item.key}`} key={item.key} className="contatti-social-card tech-card">
                <a
                  data-testid={`contatti-social-card-link-${item.key}`}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className={`contatti-social-card__link contatti-social-card__link--${item.key}`}
                  aria-label={item.label}
                  title={item.label}
                >
                  <span data-testid={`contatti-social-card-icon-${item.key}`} className={`contatti-social-card__icon contatti-social-card__icon--${item.key}`}>{socialIconMap[item.key]}</span>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
};
